import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0b0f14, #0a0a0c)",
    color: "#e7e7e9",
    padding: "32px 16px 48px",
    fontFamily: "'Pretendard', 'Noto Sans KR', system-ui, -apple-system, sans-serif",
    boxSizing: "border-box",
  },
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.32)",
    borderRadius: "18px",
    padding: "18px 16px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  titleWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  badge: {
    background: "linear-gradient(120deg, #fb923c, #f97316)",
    color: "#130903",
    padding: "6px 12px",
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "12px",
    border: "1px solid rgba(255,255,255,0.12)",
    display: "inline-block",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.02em",
  },
  helper: {
    margin: 0,
    color: "#cbd5e1",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  actionsRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  ghostBtn: {
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#f8fafc",
    fontWeight: 700,
    cursor: "pointer",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "12px",
    marginTop: "12px",
  },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.05)",
    color: "#f8fafc",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.05)",
    color: "#f8fafc",
    boxSizing: "border-box",
    minHeight: "120px",
    resize: "vertical",
  },
  label: {
    fontWeight: 700,
    color: "#f8fafc",
    marginBottom: "6px",
    display: "block",
  },
  primaryBtn: {
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "linear-gradient(120deg, #fb923c, #f97316)",
    color: "#130903",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 12px 28px rgba(249,115,22,0.26)",
  },
  listGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "12px",
    marginTop: "12px",
  },
  tile: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    padding: "12px",
    boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
    display: "grid",
    gap: "8px",
  },
  thumb: {
    width: "100%",
    height: "200px",
    maxHeight: "240px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.06)",
    objectFit: "contain",
    minHeight: "140px",
    background: "rgba(255,255,255,0.03)",
    display: "block",
  },
  smallText: {
    color: "#cbd5e1",
    fontSize: "13px",
  },
};

export default function AdManage() {
  const [cookies] = useCookies(["uuid"]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [duration, setDuration] = useState(8);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const res = await fetch("/ads");
        const data = await res.json();
        const list = Array.isArray(data) ? data : Array.isArray(data.ads) ? data.ads : [];
        setAds(list);
      } catch (err) {
        console.error("광고 목록 조회 오류:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result || "");
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    let finalImageUrl = imageUrl; // 우선 URL 입력값

    // 1) 파일 업로드가 있다면 presign → S3 PUT
    if (imageFile) {
      try {
        const presignRes = await fetch("/ads/presign", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: imageFile.name,
            fileType: imageFile.type,
          }),
        });
        if (!presignRes.ok) throw new Error("Presigned URL 발급 실패");
        const { uploadUrl, fileUrl } = await presignRes.json();

        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": imageFile.type },
          body: imageFile,
        });
        if (!putRes.ok) throw new Error("S3 업로드 실패");

        finalImageUrl = fileUrl;
      } catch (err) {
        console.error(err);
        alert("이미지 업로드 중 오류가 발생했습니다.");
        return;
      }
    }

    // 2) 광고 메타 저장 (JSON)
    try {
      const res = await fetch("/ads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description,
          link,
          duration,
          image_url: finalImageUrl,
        }),
      });
      if (!res.ok) throw new Error("업로드 실패");
      const data = await res.json();
      const list = Array.isArray(data) ? data : Array.isArray(data.ads) ? data.ads : [];
      setAds(list.length ? list : ads);
      alert("광고가 저장되었습니다.");
    } catch (err) {
      console.error(err);
      alert("광고 저장 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async (id, image_url) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      const res = await fetch(`/ads/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_url }),
      });
      if (!res.ok) throw new Error("삭제 실패");
      const data = await res.json();
      // 서버가 최신 목록을 반환하면 그대로 반영, 아니면 기존 방식 fallback
      if (Array.isArray(data)) {
        setAds(data);
      } else if (Array.isArray(data?.ads)) {
        setAds(data.ads);
      } else {
        setAds((prev) => prev.filter((ad) => ad.id !== id));
      }
    } catch (err) {
      console.error(err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.headerRow}>
            <div style={styles.titleWrap}>
              <span style={styles.badge}>Speed BOT</span>
              <h2 style={styles.title}>광고 관리</h2>
              <p style={styles.helper}>메인 하단 광고 이미지를 업로드/수정하고 노출 시간을 설정하세요.</p>
            </div>
            <div style={styles.actionsRow}>
              <button style={styles.ghostBtn} onClick={() => window.history.back()}>뒤로가기</button>
            </div>
          </div>

          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>제목</label>
              <input style={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="광고 제목" />
            </div>
            <div>
              <label style={styles.label}>링크 (클릭 이동)</label>
              <input style={styles.input} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://example.com" />
            </div>
            <div>
              <label style={styles.label}>노출 시간(초)</label>
              <input
                style={styles.input}
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value) || 1)}
                placeholder="8"
              />
            </div>
            <div>
              <label style={styles.label}>이미지 URL (선택)</label>
              <input style={styles.input} value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="이미지 주소" />
            </div>
            <div>
              <label style={styles.label}>이미지 업로드 (선택)</label>
              <input style={styles.input} type="file" accept="image/*" onChange={handleFileChange} />
              {imagePreview && (
                <img src={imagePreview} alt="preview" style={{ marginTop: 8, width: "100%", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)" }} />
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
            <button style={styles.primaryBtn} onClick={handleSave}>저장</button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.headerRow}>
            <div style={styles.titleWrap}>
              <h3 style={styles.title}>등록된 광고</h3>
              <p style={styles.helper}>삭제 시 즉시 목록에서 제거됩니다.</p>
            </div>
          </div>
          {loading ? (
            <p style={styles.helper}>불러오는 중...</p>
          ) : ads.length === 0 ? (
            <p style={styles.helper}>등록된 광고가 없습니다.</p>
          ) : (
            <div style={styles.listGrid}>
              {ads.map((ad) => (
                <div key={ad.id || ad.image_url || ad.title} style={styles.tile}>
                  {ad.image_url ? (
                    <img src={ad.image_url} alt={ad.title || "ad"} style={styles.thumb} />
                  ) : (
                    <div style={{ ...styles.thumb, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", fontSize: "13px" }}>
                      이미지 없음
                    </div>
                  )}
                  <div style={{ color: "#f8fafc", fontWeight: 800 }}>{ad.title || "제목 없음"}</div>
                  <div style={styles.smallText}>링크: {ad.link || "-"}</div>
                  <div style={styles.smallText}>노출: {ad.duration || ad.duration_seconds || 8} 초</div>
                  <button style={styles.ghostBtn} onClick={() => handleDelete(ad.id, ad.image_url)}>
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

